"use client";

import Link from "next/link";
import React, { useState } from "react";
import { usePathname } from "next/navigation";
import { ChevronDown, Menu, X, LogIn, Globe } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useLanguage } from "../hooks/useLanguage";
import PublicNavigation from "./PublicNavigation";

const Header = () => {
  const pathname = usePathname();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const { language, changeLanguage, t, availableLanguages } = useLanguage();

  // Don't show header on admin/ljpr routes
  if (pathname?.startsWith("/ljpr")) {
    return null;
  }

  return (
    <header className="header-sticky bg-primary no-horizontal-scroll relative">
      <div className="relative">
        {/* Top Blue Bar with Login and Language */}
        <div className="bg-destructive w-full">
          <div className="container-full mx-auto px-2 sm:px-4 py-1">
            <div className="flex items-center justify-end gap-2">
              {/* Language Selector - Top Bar */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="h-6 px-2 text-xs bg-transparent border border-white/30 hover:bg-white/20 text-white">
                    <Globe className="h-3 w-3 mr-1" />
                    <span className="hidden sm:inline">
                      {language === "hi" ? "हिंदी" : "English"}
                    </span>
                    <span className="sm:hidden">
                      {language === "hi" ? "हिं" : "EN"}
                    </span>
                    <ChevronDown className="ml-1 h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="border-none shadow-lg bg-card">
                  {availableLanguages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={language === lang.code ? "bg-accent" : ""}
                    >
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Admin Login Button - Top Bar */}
              {/* <Link href="/ljpr/login">
                <Button className="h-6 px-2 text-xs bg-transparent border border-white/30 hover:bg-white/20 text-white">
                  <LogIn className="h-3 w-3 mr-1" />
                  <span className="hidden sm:inline">Admin Login</span>
                  <span className="sm:hidden">Login</span>
                </Button>
              </Link> */}
            </div>
          </div>
        </div>

        <div className="bg-primary text-white relative overflow-visible">
          <div className="container-full mx-auto px-2 sm:px-4 py-2 sm:py-3 max-w-full overflow-visible">
            <div className="flex items-center justify-between min-w-0 gap-2 overflow-visible">
              {/* Logo Section */}
              <div className="flex items-center flex-shrink-0 min-w-0 max-w-none md:max-w-[55%]">
                <Link
                  href="/"
                  className="flex items-center gap-2 sm:gap-3 min-w-0 cursor-pointer"
                >
                  <img
                    src="/images/favicon.png"
                    alt="LJP Logo"
                    className="w-10 sm:w-12 md:w-14 h-auto cursor-pointer"
                  />
                  <span className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold leading-tight whitespace-normal break-words md:whitespace-nowrap md:truncate">
                    {t("homepage.hero.title")}{" "}
                    {t("homepage.hero.titleHighlight")}
                  </span>
                </Link>
              </div>

              {/* Desktop Navigation and Controls */}
              <div className="hidden lg:flex items-center gap-1 sm:gap-1.5 md:gap-2 xl:gap-2.5 flex-shrink-0 overflow-visible">
                {/* Navigation Menu - Desktop Only */}
                <PublicNavigation className="overflow-visible text-xs sm:text-sm md:text-sm" />

                {/* Donation Button - Desktop */}
                <Button className="whitespace-nowrap h-6 sm:h-7 md:h-8 xl:h-8 px-2 sm:px-2 md:px-2.5 xl:px-3 rounded-md cursor-pointer bg-white text-[11px] sm:text-xs md:text-sm flex-shrink-0 language-transition hover:bg-gray-50 transition-all duration-200">
                  <Link
                    href="https://membership.ljpr.in/?type=donor"
                    className="text-black whitespace-nowrap"
                  >
                    {t("donations.donateNow")}
                  </Link>
                </Button>

                {/* Apply as Candidate - Desktop removed as per request */}
              </div>

              {/* Mobile Controls */}
              <div className="lg:hidden flex items-center gap-1 sm:gap-2 flex-shrink-0 min-w-0">
                {/* Mobile Menu Toggle */}
                <Button
                  size="sm"
                  className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 p-0 bg-transparent border border-white/30 hover:bg-white/10 flex-shrink-0 rounded-md transition-all duration-200"
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                >
                  {showMobileMenu ? (
                    <X className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5" />
                  ) : (
                    <Menu className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {showMobileMenu && (
            <div className="lg:hidden border-t border-white/20 overflow-hidden">
              <div className="container-full mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 max-w-full">
                <PublicNavigation className="text-base" isMobile={true} />
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <Link
                    href="https://membership.ljpr.in/?type=donor"
                    className="w-full"
                  >
                    <Button className="w-full bg-white text-black text-sm hover:bg-gray-50">
                      {t("donations.donateNow")}
                    </Button>
                  </Link>
                  {/* Apply as Candidate - Mobile removed as per request */}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Border - Green */}
      <div className="h-1 sm:h-2 bg-success w-full"></div>
    </header>
  );
};

export default Header;
